import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import {
  fetchCurrentUser,
  logout,
  restoreAuth,
  selectIsAuthenticated,
  selectToken,
} from '@/features/auth/authSlice'
import { STORAGE_KEYS, getAuthTokenFromStorage } from '@/lib/utils/storage'
import { setAuthToken } from '@/lib/api/apiClient'
import type { AuthGuardProps } from '@/features/auth/types'

/**
 * Keeps the Redux auth session in sync with `localStorage` (other tabs, manual
 * clears) and backfills user data when a token is restored without it.
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const dispatch = useAppDispatch()
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const currentToken = useAppSelector(selectToken)

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (
        event.storageArea !== localStorage ||
        (event.key !== STORAGE_KEYS.authToken && event.key !== 'authToken')
      ) {
        return
      }

      const nextToken = getAuthTokenFromStorage()

      if (!nextToken) {
        setAuthToken(null)
        dispatch(logout())
        return
      }

      if (nextToken !== currentToken) {
        setAuthToken(nextToken)
        dispatch(restoreAuth())
        void dispatch(fetchCurrentUser())
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [currentToken, dispatch])

  // Page reload restores the token from storage but not the in-memory user.
  const user = useAppSelector((state) => state.auth.user)
  const token = useAppSelector((state) => state.auth.token)

  useEffect(() => {
    if (isAuthenticated && token && !user) {
      void dispatch(fetchCurrentUser())
    }
  }, [isAuthenticated, token, user, dispatch])

  return <>{children}</>
}
